import { API_BASE_URL } from "@/config";
import Swal from "sweetalert2";

export default {
  name: "UsageComponent",
  props: {
    service_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      billing_logs: [],
      filtered_logs: [],
      filter_date_from: "",
      filter_date_to: "",
      filter_service_name: "",
      filter_company: "",
      filter_billing_type: "",
      filter_source: "",
      usage_stats: {
        total_requests: 0,
        total_revenue: 0,
        total_cost: 0,
        profit_margin: 0,
        success_rate: 0,
        successful_requests: 0,
        failed_requests: 0,
      },

      logs_current_page: 1,
      logs_items_per_page: 10,
    };
  },
  computed: {
    ApexCharts() {
      return window.ApexCharts || null;
    },
    unique_services() {
      return [
        ...new Set(this.billing_logs.map((log) => log.service.service_name)),
      ];
    },
    unique_companies() {
      return [
        ...new Set(
          this.billing_logs
            .filter((log) => log.created_by.company_name)
            .map((log) => log.created_by.company_name)
        ),
      ];
    },
    paginated_billing_logs() {
      const start = (this.logs_current_page - 1) * this.logs_items_per_page;
      const end = start + this.logs_items_per_page;
      return this.billing_logs.slice(start, end);
    },
    logs_total_pages() {
      return Math.ceil(this.billing_logs.length / this.logs_items_per_page);
    },
  },
  async mounted() {
    const load_script = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          const check = () => {
            if (src.includes("apexcharts") && window.ApexCharts) {
              resolve();
            } else {
              setTimeout(check, 50);
            }
          };
          return check();
        }
        const s = document.createElement("script");
        s.src = src;
        s.async = false;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    await load_script("https://cdn.jsdelivr.net/npm/apexcharts");
    this.fetch_usage_logs();
  },
  methods: {
    fetch_usage_logs() {
      const TOKEN = localStorage.getItem("token");
      Swal.fire({
        title: "Loading Usage Logs",
        text: "Please wait while we fetch the usage logs.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
        willClose: () => {
          Swal.hideLoading();
        },
      });
      // Fetch usage logs from the API
      fetch(
        `${API_BASE_URL}/verify_api/instant_verify_api/billing_logs/filter/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${TOKEN}`,
          },
          body: JSON.stringify({
            service_id: this.service_id,
          }),
        }
      )
        .then((response) => response.json())
        .then((data) => {
          Swal.close();
          this.billing_logs = data;
          this.process_usage_data();
        })
        .catch(() => {
          Swal.close();
        });
    },
    // Calculate overall usage statistics
    calculate_usage_stats() {
      this.usage_stats = {
        total_requests: this.billing_logs.length,
        total_revenue: this.billing_logs.reduce(
          (sum, log) => sum + parseFloat(log.client_price),
          0
        ),
        total_cost: this.billing_logs.reduce(
          (sum, log) => sum + parseFloat(log.service_cost),
          0
        ),
        successful_requests: this.billing_logs.filter(
          (log) => log.status === "success"
        ).length,
        failed_requests: this.billing_logs.filter(
          (log) => log.status !== "success"
        ).length,
      };
      this.usage_stats.profit_margin = (
        ((this.usage_stats.total_revenue - this.usage_stats.total_cost) /
          this.usage_stats.total_revenue) *
        100
      ).toFixed(2);
      this.usage_stats.success_rate = (
        (this.usage_stats.successful_requests /
          this.usage_stats.total_requests) *
        100
      ).toFixed(2);
    },

    // Create overall usage timeline chart with ApexCharts
    create_overall_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#overall-usage-chart");
      if (container) container.innerHTML = "";

      const date_groups = this.group_by(this.billing_logs, (log) =>
        new Date(log.date_created).toLocaleDateString()
      );

      const log_dates = Object.keys(date_groups).sort();
      const success_data = log_dates.map(
        (log_date) =>
          date_groups[log_date].filter(
            (log) => log.billing_type.billing_name === "Success"
          ).length
      );
      const failed_data = log_dates.map(
        (log_date) =>
          date_groups[log_date].filter(
            (log) => log.billing_type.billing_name === "Failed"
          ).length
      );
      const user_error_data = log_dates.map(
        (log_date) =>
          date_groups[log_date].filter(
            (log) => log.billing_type.billing_name === "User Error"
          ).length
      );
      const unavailable_data = log_dates.map(
        (log_date) =>
          date_groups[log_date].filter(
            (log) => log.billing_type.billing_name === "Unavailable"
          ).length
      );

      const options = {
        chart: {
          type: "area",
          height: 400,
          stacked: true,
          zoom: { enabled: true },
        },
        series: [
          {
            name: "Success",
            data: success_data,
            color: "#4BC0C0",
          },
          {
            name: "Failed",
            data: failed_data,
            color: "#FF6384",
          },
          {
            name: "User Error",
            data: user_error_data,
            color: "#FFCD56",
          },
          {
            name: "Unavailable",
            data: unavailable_data,
            color: "#C9CBCF",
          },
        ],
        xaxis: {
          categories: log_dates,
          title: { text: "Date" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        fill: {
          type: "solid",
          opacity: 0.7,
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        legend: {
          position: "top",
        },
        dataLabels: {
          enabled: false,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create workflow service usage chart
    create_workflow_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#workflow-usage-chart");
      if (container) container.innerHTML = "";

      const workflow_groups = this.group_by(
        this.billing_logs.filter((log) => log.source === "Workflow"),
        (log) => log.workflow.workflow_name
      );

      const options = {
        chart: {
          type: "bar",
          height: 350,
        },
        series: [
          {
            name: "Requests",
            data: Object.values(workflow_groups).map((group) => group.length),
          },
        ],
        xaxis: {
          categories: Object.keys(workflow_groups),
          title: { text: "Workflow" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        colors: ["#008FFB"],
        dataLabels: {
          enabled: true,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create client usage chart
    create_client_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#client-usage-chart");
      if (container) container.innerHTML = "";

      const client_groups = this.group_by(
        this.billing_logs.filter((log) => log.source === "Instant Verify"),
        (log) => log.created_by.company_name
      );

      const options = {
        chart: {
          type: "bar",
          height: 350,
        },
        series: [
          {
            name: "Requests",
            data: Object.values(client_groups).map((group) => group.length),
          },
        ],
        xaxis: {
          categories: Object.keys(client_groups),
          title: { text: "Client" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        colors: ["#00E396"],
        dataLabels: {
          enabled: true,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create hourly usage chart
    create_hourly_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#hourly-usage-chart");
      if (container) container.innerHTML = "";

      const hourly_groups = this.group_by(this.billing_logs, (log) =>
        new Date(log.date_created).getHours()
      );
      const hours = Array.from({ length: 24 }, (_, i) => i);
      const hourly_data = hours.map(
        (hour) => (hourly_groups[hour] || []).length
      );

      const options = {
        chart: {
          type: "bar",
          height: 350,
        },
        series: [
          {
            name: "Requests",
            data: hourly_data,
          },
        ],
        xaxis: {
          categories: hours,
          title: { text: "Hour of Day" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        colors: ["#FEB019"],
        dataLabels: {
          enabled: false,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create status distribution pie chart
    create_status_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#status-chart");
      if (container) container.innerHTML = "";

      const billing_type_groups = this.group_by(
        this.billing_logs,
        (log) => log.billing_type.billing_name
      );

      const options = {
        chart: {
          type: "pie",
          height: 350,
        },
        series: Object.values(billing_type_groups).map((group) => group.length),
        labels: Object.keys(billing_type_groups),
        colors: ["#4BC0C0", "#FF6384", "#FFCD56", "#C9CBCF", "#FF9F40"],
        legend: {
          position: "bottom",
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + "%";
          },
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create cost vs revenue scatter plot
    create_cost_revenue_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#cost-revenue-chart");
      if (container) container.innerHTML = "";

      const client_logs = this.group_by(
        this.billing_logs,
        (log) => log.created_by.company_name
      );
      const series_data = [];

      Object.entries(client_logs).forEach(([client_name, logs]) => {
        const daily_logs = this.group_by(logs, (log) =>
          new Date(log.date_created).toLocaleDateString()
        );

        const scatter_points = Object.entries(daily_logs).map(
          ([date, day_logs]) => ({
            x: new Date(date).getTime(),
            y: day_logs.length,
            z: day_logs.reduce(
              (sum, log) => sum + parseFloat(log.client_price),
              0
            ),
          })
        );

        series_data.push({
          name: client_name,
          data: scatter_points,
        });
      });

      const options = {
        chart: {
          type: "scatter",
          height: 350,
          zoom: { enabled: true },
        },
        series: series_data,
        xaxis: {
          type: "datetime",
          title: { text: "Date" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        markers: {
          size: 6,
          hover: { size: 8 },
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create revenue by client chart
    create_revenue_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#revenue-chart");
      if (container) container.innerHTML = "";

      const revenue_groups = this.group_by(
        this.billing_logs,
        (log) => log.created_by.company_name
      );

      const options = {
        chart: {
          type: "bar",
          height: 350,
        },
        series: [
          {
            name: "Revenue",
            data: Object.values(revenue_groups).map((group) =>
              group.reduce((sum, log) => sum + parseFloat(log.client_price), 0)
            ),
          },
        ],
        xaxis: {
          categories: Object.keys(revenue_groups),
          title: { text: "Client" },
        },
        yaxis: {
          title: { text: "Revenue (R)" },
          labels: {
            formatter: function (val) {
              return "R" + val.toFixed(2);
            },
          },
        },
        colors: ["#775DD0"],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return "R" + val.toFixed(2);
          },
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Process all data and create charts
    process_usage_data() {
      this.calculate_usage_stats();

      this.$nextTick(() => {
        this.create_overall_usage_chart();
        this.create_workflow_usage_chart();
        this.create_client_usage_chart();
        this.create_hourly_usage_chart();
        this.create_status_chart();
        this.create_cost_revenue_chart();
        this.create_revenue_chart();
      });
    },

    // Process data for client revenue (Bar Chart)

    next_logs_page() {
      if (this.logs_current_page < this.logs_total_pages) {
        this.logs_current_page++;
      }
    },
    previous_logs_page() {
      if (this.logs_current_page > 1) {
        this.logs_current_page--;
      }
    },
    group_by(array, key) {
      return array.reduce((result, item) => {
        const k = typeof key === "function" ? key(item) : item[key];
        if (!result[k]) result[k] = [];
        result[k].push(item);
        return result;
      }, {});
    },
    reset_filters() {
      // Reset all filter values
      this.filter_date_from = "";
      this.filter_date_to = "";
      this.filter_service_name = "";
      this.filter_company = "";
      this.filter_billing_type = "";
      this.filter_source = "";

      // Restore original data if exists
      if (this._originalLogs) {
        this.billing_logs = [...this._originalLogs];
      }

      // Reset pagination to first page
      this.logs_current_page = 1;

      // Clear and rebuild charts
      this.overall_usage_data = [];
      this.workflow_service_data = [];
      this.client_usage_data = [];
      this.hourly_usage_data = [];
      this.status_data = [];
      this.cost_revenue_data = [];
      this.revenue_data = [];

      this.$nextTick(() => {
        this.process_usage_data();
      });
    },
    apply_filters() {
      // Store the original logs if not already stored
      if (!this._originalLogs) {
        this._originalLogs = [...this.billing_logs];
      }

      // Apply filters
      this.billing_logs = this._originalLogs.filter((log) => {
        const date_match =
          (!this.filter_date_from ||
            new Date(log.date_created) >= new Date(this.filter_date_from)) &&
          (!this.filter_date_to ||
            new Date(log.date_created) <= new Date(this.filter_date_to));
        const service_match =
          !this.filter_service_name ||
          log.service.service_name === this.filter_service_name;
        const company_match =
          !this.filter_company ||
          log.created_by.company_name === this.filter_company;
        const billing_match =
          !this.filter_billing_type ||
          log.billing_type.billing_name === this.filter_billing_type;
        const source_match =
          !this.filter_source || log.source === this.filter_source;

        return (
          date_match &&
          service_match &&
          company_match &&
          billing_match &&
          source_match
        );
      });

      // Reset pagination to first page
      this.logs_current_page = 1;

      // Rebuild charts with filtered data
      this.$nextTick(() => {
        this.process_usage_data();
      });
    },
  },
  beforeUnmount() {
    const chart_ids = [
      "#overall-usage-chart",
      "#workflow-usage-chart",
      "#client-usage-chart",
      "#hourly-usage-chart",
      "#status-chart",
      "#cost-revenue-chart",
      "#revenue-chart",
    ];
    chart_ids.forEach((id) => {
      const container = document.querySelector(id);
      if (container) container.innerHTML = "";
    });
    // Clean up any dynamically loaded scripts if necessary
    const scripts = ["https://cdn.jsdelivr.net/npm/apexcharts"];
    scripts.forEach((src) => {
      const script = document.querySelector(`script[src="${src}"]`);
      if (script) {
        script.remove();
      }
    });
  },
};
